// Phase 6.3.1 — focus utilities shared by the overlay primitives
// (Modal, SidePanel). Keeps the focus-trap logic in one place so
// every overlay traps Tab the same way.

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** Every tabbable element inside `container`, in DOM order. */
export function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
}

/** Handle a Tab keydown to cycle focus within `container` (a focus
 *  trap). Returns true if it handled the event. */
export function trapTabKey(
  e: KeyboardEvent | React.KeyboardEvent,
  container: HTMLElement | null,
): boolean {
  if (e.key !== "Tab") return false;
  const focusable = getFocusable(container);
  if (focusable.length === 0) {
    // Nothing focusable — keep focus on the container itself.
    e.preventDefault();
    return true;
  }
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey) {
    if (active === first || !container?.contains(active)) {
      e.preventDefault();
      last.focus();
      return true;
    }
  } else if (active === last || !container?.contains(active)) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}
